import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';

import IbSwitch from '../../../../../components/common/IbSwitch';
import IbIcon from '../../../../../components/common/IbIcon';
import { getToolsetTypeIconName, getToolsetTypeName } from '../utils';
import { ToolsetModel, ToolsetState } from '../../../../../../gamma-api';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTooltip from '../../../../../components/common/IbTooltip';
import IbDivider from '../../../../../components/common/IbDivider';
import { formatDateFull, getTranslateFunc } from '../../../../../utils/stringUtil';
import IbButton from '../../../../../components/common/IbButton';
import IbContextMenu from '../../../../../components/common/IbContextMenu';

import {
  CONTEXT_CLASS_NAME,
  HEADER_CLASS_NAME,
  INFO_CLASS_NAME,
  MAIN_CLASS_NAME,
  SKELETON_HEIGHT,
  SKELETON_WIDTH,
  TITLE_CLASS_NAME,
} from './const';

export interface IBaseToolsetListItemProps {
  name?: string;
  toolCount?: number;
  toolset: ToolsetModel;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  loading?: boolean;
  onSetup?: (toolset: ToolsetModel) => void;
  onDelete?: (toolset: ToolsetModel) => void;
  onToolsetStateChange?: (toolset: ToolsetModel, state: ToolsetState) => void;
}

const BaseToolsetListItem: React.FC<IBaseToolsetListItemProps> = ({
  name,
  toolCount,
  toolset,
  action,
  footer,
  loading,
  onSetup,
  onDelete,
  onToolsetStateChange,
}) => {
  const { i18n } = useTranslation();
  const [contextVisible, setContextVisible] = useState(false);

  const translate = getTranslateFunc('Agent: Toolset: List: Item:');

  const onStateChange = (state: boolean) =>
    onToolsetStateChange?.(toolset, state ? ToolsetState.Enabled : ToolsetState.Disabled);

  const displayName = name ?? getToolsetTypeName(toolset.type);

  const renderContext = () => {
    const onMoreButtonClick = () => {
      setContextVisible(true);
    };

    const onMenuClose = () => {
      setContextVisible(false);
    };

    const onSetupSelect = () => {
      onSetup?.(toolset);
    };

    const onDeleteSelect = () => {
      onDelete?.(toolset);
    };

    const menuItems = [];

    if (onSetup) {
      menuItems.push({
        icon: <IbIcon iconName="setting-two" size={20} />,
        text: translate('Setup'),
        onSelect: onSetupSelect,
      });
    }

    if (onDelete) {
      menuItems.push({
        icon: <IbIcon iconName="delete" size={20} />,
        text: translate('Delete'),
        onSelect: onDeleteSelect,
      });
    }

    return (
      <IbContextMenu menuItems={menuItems} visible={contextVisible} onClose={onMenuClose}>
        <IbButton
          className={CONTEXT_CLASS_NAME}
          icon={<IbIcon iconName="more-one" />}
          type="icon"
          onClick={onMoreButtonClick}
        />
      </IbContextMenu>
    );
  };

  const renderHeader = () => (
    <div className={HEADER_CLASS_NAME}>
      <IbSwitch checked={toolset.state === ToolsetState.Enabled} size="small" onChange={onStateChange} />
      <div className={TITLE_CLASS_NAME}>
        <IbIcon iconName={getToolsetTypeIconName(toolset.type)} size={16} />
        {loading ? (
          <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
        ) : (
          <>
            <IbTooltip title={displayName}>
              <IbTypography.Paragraph ellipsis strong type="secondary">
                {displayName}
              </IbTypography.Paragraph>
            </IbTooltip>
            {action}
          </>
        )}
      </div>
      <div className={INFO_CLASS_NAME}>
        {toolCount != undefined && (
          <>
            <IbTypography.Paragraph disabled type="secondary">
              {translate('Tools: Count', { count: toolCount })}
            </IbTypography.Paragraph>
            <IbDivider orientation="center" type="vertical" />
          </>
        )}
        <IbTypography.Paragraph disabled type="secondary">
          {toolset.created_on === toolset.modified_on
            ? translate('Created on', {
                date: formatDateFull(toolset.created_on, i18n.language),
              })
            : translate('Modified on', {
                date: formatDateFull(toolset.modified_on, i18n.language),
              })}
        </IbTypography.Paragraph>
      </div>
      {renderContext()}
    </div>
  );

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderHeader()}
      {footer}
    </div>
  );
};

export default BaseToolsetListItem;
